import defaultBackground from "@/images/backgrounds/lobby-background1.webp"

// Eagerly bundle every custom game background PNG in the backgrounds folder.
// Using globEager (the Vite 2 eager glob API) means any new lobby-background*.png
// the user drops in is picked up automatically with no code change required.
const backgroundModules = import.meta.globEager<{ default: string }>(
  "../images/backgrounds/lobby-background*.png"
)

// Small pre-generated previews (see scripts/generate-background-thumbs.cjs) so the
// settings modal doesn't have to decode dozens of multi-megapixel full-res images
// just to render a handful of small thumbnails.
const thumbnailModules = import.meta.globEager<{ default: string }>(
  "../images/backgrounds/thumbs/*.webp"
)

export interface IGameBackground {
  /** Stable identifier used for persistence (the file name, or "default"). */
  id: string
  /** Human friendly label shown under each thumbnail. */
  name: string
  /** Bundled asset URL to render. */
  url: string
  /** Small preview asset URL, for use in thumbnail grids. */
  thumbnailUrl: string
}

const fileNameFromPath = (path: string): string => path.split("/").pop() || path

// Sort by the trailing number so backgrounds appear in a predictable order
// regardless of the order globEager returns them in. Files without a number
// (e.g. "lobby-background.png") sort first.
const numberFromFileName = (fileName: string): number => {
  const match = fileName.match(/lobby-background(\d+)\.png$/)
  return match ? parseInt(match[1], 10) : 0
}

const thumbnailUrlFor = (fileName: string, fallbackUrl: string): string => {
  const base = fileName.replace(/\.[^.]+$/, "")
  const thumbEntry = Object.entries(thumbnailModules).find(([path]) =>
    fileNameFromPath(path) === `${base}.webp`
  )
  return thumbEntry ? thumbEntry[1].default : fallbackUrl
}

const customBackgrounds: IGameBackground[] = Object.entries(backgroundModules)
  .map(([path, mod]) => ({ fileName: fileNameFromPath(path), url: mod.default }))
  .sort((a, b) => numberFromFileName(a.fileName) - numberFromFileName(b.fileName))
  .map((item, index) => ({
    id: item.fileName,
    name: `Background ${index + 1}`,
    url: item.url,
    thumbnailUrl: thumbnailUrlFor(item.fileName, item.url),
  }))

// The default keeps the game's original look for players who never pick one.
export const DEFAULT_BACKGROUND: IGameBackground = {
  id: "default",
  name: "Default",
  url: defaultBackground,
  thumbnailUrl: thumbnailUrlFor("lobby-background1.webp", defaultBackground),
}

export const gameBackgrounds: IGameBackground[] = [
  DEFAULT_BACKGROUND,
  ...customBackgrounds,
]

export const resolveBackgroundUrl = (id?: string): string =>
  gameBackgrounds.find((bg) => bg.id === id)?.url ?? DEFAULT_BACKGROUND.url

const STORAGE_KEY = "game.selectedBackgroundId"

export const getStoredBackgroundId = (): string =>
  localStorage.getItem(STORAGE_KEY) || DEFAULT_BACKGROUND.id

export const setStoredBackgroundId = (id: string): void => {
  localStorage.setItem(STORAGE_KEY, id)
}
